import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "framer-motion";

import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollProgress from "./components/ScrollProgress";
import CursorGlow from "./components/CursorGlow";
import { easeLuxury } from "./components/motion";

import HomePage from "./pages/HomePage";
import BookingPage from "./pages/BookingPage";
import RoomPage from "./pages/RoomPage";
import GalleryPage from "./pages/GalleryPage";
import AmenitiesPage from "./pages/AmenitiesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import PoliciesPage from "./pages/PoliciesPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AdminApp from "./admin/AdminApp";
import Seo, { buildLodgingSchema } from "./components/Seo";
import { SiteContentProvider, useContent } from "./context/SiteContentContext";
import RulesPage from "./pages/RulesPage";
import CancellationPolicyPage from "./pages/CancellationPolicyPage";
import BookingConfirmationPage from "./pages/BookingConfirmationPage";
import SeoLandingPage from "./pages/SeoLandingPage";
import SeoNotFound from "./pages/SeoNotFound";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const ROUTE_SEO = {
  "/": {
    title: "ROOM XIII · Private Luxury Stay for Couples in Zirakpur | Airbnb near Chandigarh",
    description: "ROOM XIII is a private luxury Airbnb-style suite for couples in Zirakpur, near Chandigarh and Mohali. Self check-in, hourly & overnight stays, total discretion.",
  },
  "/room": { title: "The Suite | Luxury Private Room for Couples in Zirakpur – ROOM XIII", description: "Explore the ROOM XIII suite: plush bed, cinematic lighting, lounge, kitchenette and private washroom. A private luxury room for couples in Zirakpur." },
  "/booking": { title: "Book a Private Room in Zirakpur | Hourly & Overnight – ROOM XIII", description: "Book ROOM XIII online. Hourly stays from ₹500 and overnight stays with smart-lock self check-in in Zirakpur." },
  "/gallery": { title: "Gallery | ROOM XIII Luxury Suite, Zirakpur", description: "Photos of the ROOM XIII suite, lounge, kitchenette and washroom – a private luxury stay for couples in Zirakpur." },
  "/amenities": { title: "Amenities | ROOM XIII Private Couple Suite in Zirakpur", description: "Mood lighting, soundproofing, Wi-Fi, TV, kitchenette, snacks and more. See everything included at ROOM XIII, Zirakpur." },
  "/about": { title: "About ROOM XIII | Private Boutique Stay in Zirakpur", description: "The story behind ROOM XIII – a discreet, cinematic boutique stay designed exclusively for couples in Zirakpur, Punjab." },
  "/contact": { title: "Contact ROOM XIII | Zirakpur, Patiala Road", description: "Contact ROOM XIII by phone, WhatsApp or email for bookings and enquiries. Patiala Road, Zirakpur, Punjab." },
  "/faq": { title: "FAQ | Booking, Privacy & Timings – ROOM XIII Zirakpur", description: "Answers on privacy, ID requirements, hourly bookings, check-in times and cancellation at ROOM XIII, Zirakpur." },
  "/rules": { title: "House Rules | Check-in, Check-out & Cancellation – ROOM XIII", description: "Check-in and check-out times, guest requirements, cancellation and non-refundable booking rules at ROOM XIII, Zirakpur." },
  "/cancellation-policy": { title: "Cancellation Policy | Non-refundable Bookings – ROOM XIII", description: "ROOM XIII cancellation and refund policy. Bookings are non-refundable once confirmed. Read the details before you book." },
  "/policies": { title: "Policies | Privacy, Terms & Refunds – ROOM XIII", description: "ROOM XIII privacy policy, terms of stay and refund policy." },
};
const NOINDEX = ["/login", "/register", "/dashboard"];
const isNoindex = (path) => NOINDEX.includes(path) || path.startsWith("/booking/confirmation/");

function RouteSeo() {
  const { pathname } = useLocation();
  const { get } = useContent();
  const path = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  if (path.startsWith("/stays/")) return null; // landing pages set their own meta
  if (isNoindex(path)) return <Seo title="ROOM XIII" description="ROOM XIII private account area." path={path} noindex />;
  const meta = ROUTE_SEO[path];
  return meta ? <Seo {...meta} path={path} jsonLd={path === "/" ? [buildLodgingSchema(get)] : []} /> : null;
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.55, ease: easeLuxury }}
      >
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/room" element={<RoomPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/amenities" element={<AmenitiesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
          <Route path="/booking/confirmation/:id" element={<BookingConfirmationPage />} />
          <Route path="/stays/:slug" element={<SeoLandingPage />} />
          <Route path="*" element={<SeoNotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function PublicShell() {
  return (
    <>
      <ScrollProgress />
      <CursorGlow />
      <ScrollToTop />
      <RouteSeo />
      <Navbar />
      <AnimatedRoutes />
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SiteContentProvider>
          <AuthProvider>
            <Routes>
              {/* The admin is its own app: sidebar layout, no public navbar / footer / chat button */}
              <Route path="/admin/*" element={<ProtectedRoute adminOnly><AdminApp /></ProtectedRoute>} />
              <Route path="*" element={<PublicShell />} />
            </Routes>
            <Toaster
              theme="dark"
              position="top-right"
              toastOptions={{
                style: {
                  background: "#0E0E0E",
                  border: "1px solid rgba(212,175,55,0.25)",
                  color: "#F5F5F5",
                },
              }}
            />
          </AuthProvider>
        </SiteContentProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
